/**
 * Workouts' own vocabulary: the shape one vendor lane's three question kinds
 * flatten onto, and the two closed sets a reader can switch between from the
 * tweak panel.
 *
 * One lane (ymove, exercise side), three answers — a catalog page, one
 * exercise's full record, a generated plan — and the three disagree about what
 * an exercise IS. A catalog row publishes `hasVideo: true` and no address a
 * player could take; the demonstration clip only exists after a SECOND, billed
 * call (`ymove.exerciseMedia`). A generated plan's slot publishes a
 * prescription — three sets of `8-12` with 75 seconds between them — and no
 * instructions, no category and no video flags at all. So the presence of a
 * clip is not a boolean on this shape: it is a five-member state, because
 * "there is no video", "there is a video nobody has paid to resolve", "the
 * links resolved and have since died" and "the player could not start" are
 * four different sentences and only one of them is an error.
 *
 * Two rules have teeth here specifically. A pre-signed clip URL carries its own
 * expiry as DATA (`expiresAt`, exactly 48 hours after issue), so nothing on
 * this surface may treat a URL as durable — the state is recomputed against the
 * clock every render rather than decided once at capture. And `repetitions` is
 * the vendor's prescription verbatim, a range (`8-12`) or a duration
 * (`30 seconds`), never parsed into a number: the two shapes arrive on slots of
 * the same plan and collapsing them would make a warm-up hold read as three
 * repetitions.
 */
import type { ReactNode } from "react";
import type {
  CresultcardAsset,
  CresultcardDetailFrame,
  CresultcardOpenControl,
} from "@/components/ui/Cresultcard";

/**
 * The family's three compositions of one exercise. They differ in structure,
 * not in decoration: one leads with the demonstration frame, one is the dense
 * coaching text with a 52px lead, and one drops media entirely for the
 * prescription's aligned figures.
 */
export enum CworkoutcardVariant {
  /** Media-led: the demonstration frame at its own measured ratio, identity beneath. */
  DemoFrame = "Demo frame",
  /** The reference composition: identity first, the coaching text carries the card. */
  CoachingCard = "Coaching card",
  /** No media at all: sets, repetitions and rest as aligned figures. */
  LoadBoard = "Load board",
}

/**
 * How an exercise states its own execution.
 *
 * The vendor sends the same movement twice, in two registers, and both are
 * real: `description` is one 219-to-349-character paragraph that always reads
 * `Starting position: … Execution: …`, and `instructions` is four to six
 * imperative lines of at most 77 characters. Neither is a summary of the other
 * and neither is derivable from the other, so which one a card carries is an
 * editorial decision rather than a fallback.
 */
export enum CworkoutcardMoveReading {
  /** The vendor's own paragraph, verbatim. */
  Narration = "As the vendor narrates it",
  /** The numbered imperative steps. */
  Steps = "Numbered steps",
}

/**
 * Which shoot a clip came off, as the catalog's own two flags name it.
 *
 * It is an enum rather than the raw string because the catalog publishes
 * exactly two booleans for it — `hasWhiteBackgroundVideo` and
 * `hasGymShotVideo` — which is the vendor stating the set is closed. A tag
 * outside the set resolves to `null` rather than being printed unrecognised,
 * so a third shoot appearing upstream shows as an unlabelled clip instead of
 * a mislabelled one.
 */
export enum CworkoutcardShoot {
  WhiteBackground = "white-background",
  GymShot = "gym-shot",
}

/** The vendor's declared framing for a clip. Stated, never used as geometry. */
export enum CworkoutcardOrientation {
  Portrait = "portrait",
  Landscape = "landscape",
}

/**
 * What a reader can actually do about this exercise's demonstration, right
 * now.
 *
 * The five members are five different sentences, and flattening them to a
 * boolean is what produces a broken frame: a row the catalog marks
 * video-bearing has no URL until a billed call resolves one, and a URL that
 * resolved two days ago is a 403 rather than a video.
 */
export enum CworkoutcardVideoState {
  /** Clips resolved and the issue window has not closed. The frame arms. */
  Playable = "playable",
  /** Clips resolved, `expiresAt` has passed. The poster stays; the arm does not. */
  Expired = "expired",
  /** The catalog says a clip exists; no media call was spent resolving it. */
  Unresolved = "unresolved",
  /** The catalog says this exercise has no demonstration clip at all. */
  Absent = "absent",
  /** The player itself refused the source after the reader armed it. */
  Failed = "failed",
}

/** One resolved demonstration clip, with every source-owned relation intact. */
export interface CworkoutcardClip {
  readonly key: string;
  /** The pre-signed progressive file. What the native player mounts. */
  readonly videoUrl: string;
  /** The pre-signed HLS playlist, kept as evidence; not what the element mounts. */
  readonly videoHlsUrl: string | null;
  /**
   * The poster frame as a TRIPLE, never a bare URL: the frame reserves the
   * asset's own ratio before the asset loads, and it can only do that from
   * pixels somebody measured. `null` where no poster was published.
   */
  readonly poster: CresultcardAsset | null;
  /** `null` where the vendor sent a shoot tag outside its own two flags. */
  readonly shoot: CworkoutcardShoot | null;
  /** `null` where the vendor sent a framing word this family does not know. */
  readonly orientation: CworkoutcardOrientation | null;
  readonly primary: boolean;
}

/**
 * Everything a demonstration resolved to for one exercise, including the fuse
 * where there is one.
 *
 * `expiresAt` is the whole reason this is its own shape. A pre-signed vendor
 * link dies exactly 48 hours after issue, so a surface that holds one has to
 * hold the moment it stops working beside it — and recompute against the clock,
 * not against the capture.
 *
 * BOTH INSTANTS ARE NULLABLE, because a second real population reaches this
 * shape: a persisted attach block carries the DURABLE
 * `/media/exercise-video/<handle>` address, which resolves a live URL at click
 * time and therefore was never signed and never expires. Null is not a missing
 * value — it is the honest statement that nothing here is on a clock.
 */
export interface CworkoutcardMedia {
  readonly clips: readonly CworkoutcardClip[];
  /** Seconds, where anything measured one. The catalog itself publishes null. */
  readonly durationSecs: number | null;
  /** ISO 8601, when the links were issued; null where nothing was signed. */
  readonly capturedAt: string | null;
  /** ISO 8601, after which every URL above is a 403; null where the address is
   *  durable and no URL above can expire. */
  readonly expiresAt: string | null;
}

/**
 * What a generated plan prescribes for one of its slots.
 *
 * Absent on every catalog row, because a catalog row is a movement and a
 * prescription is a decision about it. `repetitions` stays a string: `8-12` and
 * `30 seconds` arrive on slots of the same plan.
 */
export interface CworkoutcardPrescription {
  readonly order: number;
  readonly sets: number;
  readonly repetitions: string;
  readonly restSeconds: number;
}

/**
 * One exercise, as all three question kinds can describe it.
 *
 * Anything one kind alone carries is optional, and absent means absent: there
 * is no placeholder, no ghost slot and no invented default. A plan slot fills
 * `title`, `muscleGroup`, `equipment`, `difficulty`, `description` and
 * `prescription`; it fills none of the catalog's flags, so `hasVideo` is
 * `false` there as a fact about the payload rather than a claim about the
 * movement — which is exactly why `videoState` is derived from the media and
 * the clock rather than read off a flag.
 */
export interface CworkoutcardData {
  /** The vendor's own handle. The row key and the single-exercise argument. */
  readonly key: string;
  /** The typed handle a single-exercise question accepts. Null where the record
   *  was read back off a persisted attach block that carried no demonstration
   *  address to recover it from. */
  readonly slug: string | null;
  /** The only field every question kind fills, and the last to give up room. */
  readonly title: string;
  /** The vendor's `Starting position: … Execution: …` paragraph, verbatim. */
  readonly description: string;
  /** The imperative steps. Empty on a plan slot's projection of the same move. */
  readonly instructions: readonly string[];
  /** The vendor's coaching cues. Empty where the kind does not carry them. */
  readonly importantPoints: readonly string[];
  /** The primary muscle, in the vendor's own vocabulary (`full_body`, `glutes`). */
  readonly muscleGroup: string;
  readonly secondaryMuscles: readonly string[];
  /** `bodyweight`, `dumbbell`, `machine` — the catalog's own equipment slug. */
  readonly equipment: string;
  /** `YOGA`, screamed as the vendor screams it. Absent off the catalog path. */
  readonly category: string | null;
  readonly difficulty: string;
  readonly exerciseTypes: readonly string[];
  /** The catalog's claim that a demonstration exists. Not an address. */
  readonly hasVideo: boolean;
  readonly hasWhiteBackgroundVideo: boolean;
  readonly hasGymShotVideo: boolean;
  /** Detail-only. `null` on a record built from a list row. */
  readonly isPremium: boolean | null;
  /** The resolved clips, where a media call was spent. */
  readonly media: CworkoutcardMedia | null;
  /** The plan's prescription for this slot. Absent on every catalog row. */
  readonly prescription: CworkoutcardPrescription | null;
}

/**
 * The row's meta facts, named so the drop order is declared against names
 * rather than positions.
 *
 * The muscle is deliberately absent from the drop order: it is the only one of
 * the three that differs on every captured row — all three rows of the mounted
 * page are `bodyweight` and `beginner` — so a line that drops it first drops
 * the only fact it was carrying.
 */
export enum CworkoutcardMetaSlot {
  Muscle = "muscle",
  Equipment = "equipment",
  Difficulty = "difficulty",
}

/**
 * The muscle line's two slots. The primary muscle is absent from the drop
 * order on purpose: it is the field a catalog page is scanned by.
 */
export enum CworkoutcardMuscleSlot {
  Secondary = "secondaryMuscles",
}

/** The plan header's own facts, keyed for the same reason. */
export enum CworkoutcardPlanFact {
  Minutes = "planMinutes",
  Difficulty = "planDifficulty",
  Muscles = "planMuscles",
  Movements = "planMovements",
}

/** One phase of a generated plan, named as the plan names it. */
export enum CworkoutcardPhase {
  Warmup = "Warm-up",
  Main = "Main",
  Cooldown = "Cool-down",
}

/** The generated plan itself — the third question kind's whole answer.
 *
 *  `difficulty` and `estimatedMinutes` are nullable because a plan read back
 *  off a persisted attach block proves only what its block actually stated: a
 *  fact nobody wrote is absent, and the header simply does not print it. */
export interface CworkoutcardPlanData {
  readonly key: string;
  readonly name: string;
  readonly difficulty: string | null;
  readonly estimatedMinutes: number | null;
  readonly muscleGroups: readonly string[];
  /** Every slot the plan carries, already tagged with the phase it belongs to. */
  readonly slots: readonly CworkoutcardPlanSlot[];
}

/** One slot of a plan: the phase it sits in, and the exercise it prescribes. */
export interface CworkoutcardPlanSlot {
  readonly phase: CworkoutcardPhase;
  readonly exercise: CworkoutcardData;
}

export interface CworkoutcardProps {
  readonly data: CworkoutcardData;
  /** Omitted, the family reads the tweak panel's registry. */
  readonly variant?: CworkoutcardVariant;
  readonly moveReading?: CworkoutcardMoveReading;
  /**
   * Handed down by the set that owns the drill. Present, the title IS the
   * item's control and its only tab stop.
   */
  readonly openControl?: CresultcardOpenControl;
}

/** What every card variant receives once the selection is resolved. */
export interface CworkoutcardVariantProps {
  readonly data: CworkoutcardData;
  readonly moveReading: CworkoutcardMoveReading;
  readonly openControl?: CresultcardOpenControl;
}

export interface CworkoutcardRowProps {
  readonly data: CworkoutcardData;
  /** The row's own index in the served page. Never the vendor's global rank. */
  readonly rank: number;
  readonly onLast?: boolean;
  readonly openControl?: CresultcardOpenControl;
}

/**
 * The rail card takes NO card variant. It is one fixed composition — a deck
 * card is a comparison object, and the three card variants are three answers to
 * "this movement, on its own" — so it stamps its own name and there is nothing
 * for a caller to select.
 */
export interface CworkoutcardRailProps {
  readonly data: CworkoutcardData;
  readonly moveReading?: CworkoutcardMoveReading;
  readonly openControl?: CresultcardOpenControl;
}

export interface CworkoutcardDetailProps {
  readonly data: CworkoutcardData;
  /** The frame every value below is stated against: the lane, the ask, the capture. */
  readonly context: string;
  /** What bounds this projection. Wraps under the frame; never truncated. */
  readonly bounds?: ReactNode;
  /** ABSENT where the header context already states it. */
  readonly provenance?: string;
  readonly moveReading?: CworkoutcardMoveReading;
  /** `Embedded` where the detail IS a set's drill pane. */
  readonly frame?: CresultcardDetailFrame;
}
